import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDownload } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, DashboardModule, Grid, Text,
} from '@domain-group/suit-theme'

import AttachmentPreview from '@components/AttachmentPreview'

import useBrand from '@hooks/useBrand'
import useCarousel from '@hooks/useCarousel'

import type { ImageModel } from '@models/types'
import type { UseCampaignPayload } from '@hooks/useCampaign'

import ImageTile from '../ImageTile'

interface ImagesProps {
  brandId: number,
  callbacks: {
    downloadMedia: UseCampaignPayload['callbacks']['downloadMedia'],
  },
  carouselPayload: ReturnType<typeof useCarousel>,
  filteredImages: ImageModel[],
  selectedIndex?: number,
}

const Images = (props: ImagesProps) => {
  const {
    brandId, callbacks: { downloadMedia }, carouselPayload, filteredImages,
  } = props

  const { callbacks: { setSelectedIndex }, selectedIndex } = carouselPayload

  const selectedImage = filteredImages[selectedIndex as number]
  const { id: selectedImageId, id_code, file_file_name } = selectedImage || {}

  const currentImageIndex = filteredImages.findIndex(image => image.id === selectedImageId)

  const brandPayload = useBrand({ id: brandId })
  const { publicLinkFontColor, publicLinkTextAccentColour } = brandPayload

  return (
    <DashboardModule
      boxProps={{
        backgroundColor: 'backgroundColor',
        border: 'none',
        marginTop: 'none',
        paddingX: 'large',
      }}
      contentBoxProps={{
        borderRadius: '5px',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
      customHeader={(<Box display="none" />)}
    >
      <Box position="relative">
        <AttachmentPreview
          // @ts-expect-error TS2739
          attachment={selectedImage}
          attachmentType="image"
          carouselPayload={carouselPayload}
        />
      </Box>

      <Box flexDirection="column">
        <Box
          alignItems={['start', 'center']}
          borderBottom="1px solid"
          borderBottomRightRadius="5px"
          borderBottomLeftRadius="5px"
          borderX="1px solid"
          borderColor="lineColor"
          flexDirection={['column-reverse', 'row']}
          justifyContent="space-between"
          padding="large"
        >
          <Box flexDirection="column" width="fit-content">
            <Text color="bodyFontColor" fontSize="small">{id_code || ''}</Text>

            <Text color="bodyFontLightColor" fontSize="xsmall" marginTop="medium">
              {file_file_name}
            </Text>
          </Box>

          <Button
            buttonStyle="primaryEdit"
            icon={<FontAwesomeIcon icon={faDownload} />}
            marginBottom={['large', 0]}
            onClick={() => downloadMedia([selectedImageId])}
            size="medium"
            width={['100%', 'fit-content']}
            style={{
              backgroundColor: publicLinkTextAccentColour,
              borderColor: publicLinkTextAccentColour,
              color: publicLinkFontColor,
            }}
          >
            Download
          </Button>
        </Box>

        <Box paddingTop="large" flexDirection="column">
          <Grid
            // @ts-expect-error TS2322
            gridAutoRows="1fr"
            gridGap="medium"
            gridTemplateColumns={['repeat(auto-fill, minmax(160px, 1fr))', 'repeat(auto-fill, minmax(150px, 1fr))']}
            gridTemplateRows={['repeat(auto-fill, minmax(125px, 1fr))', 'repeat(auto-fill, minmax(110px, 1fr))']}
          >
            {filteredImages.map((image, index) => (
              <ImageTile
                callbacks={{ handleClick: () => setSelectedIndex(index) }}
                image={image}
                isSelected={index === selectedIndex}
                key={image.id}
              />
            ))}
          </Grid>

          <Text
            color="bodyFontColor"
            fontSize="xsmall"
            fontWeight={500}
            marginTop="xlarge"
            textAlign="center"
          >
            {`${currentImageIndex + 1} of ${filteredImages.length}`}
          </Text>
        </Box>
      </Box>
    </DashboardModule>
  )
}

export default Images
